/**
 * The six rules a new password must meet, in the order the checklist lists them.
 *
 * One list for Change Password and Reset Password, mirroring the platform's `PasswordPolicy` rule for rule:
 * what the checklist ticks is what the server accepts. "Special" is defined the way the server defines it --
 * anything that is not a letter, a digit or whitespace -- so a character the screen ticks is never one the
 * server refuses.
 *
 * "No spaces" mirrors the platform's whitespace refusal ("Password must not contain spaces"): any whitespace
 * anywhere in the password -- leading, trailing or inside -- is refused, never trimmed away. It reads as unmet
 * while nothing is typed, like every other rule, so an empty field never shows a tick.
 */
export interface PasswordRule {
  id: 'length' | 'uppercase' | 'lowercase' | 'number' | 'special' | 'noSpaces';
  label: string;
  test: (password: string) => boolean;
}

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 128;

/** The platform's own words for a password holding whitespace. */
export const PASSWORD_NO_SPACES_MESSAGE = 'Password must not contain spaces';

export const PASSWORD_RULES: readonly PasswordRule[] = [
  {
    id: 'length',
    label: `Minimum ${String(PASSWORD_MIN_LENGTH)} characters`,
    test: (password) => password.length >= PASSWORD_MIN_LENGTH,
  },
  { id: 'uppercase', label: 'At least 1 uppercase letter', test: (password) => /\p{Lu}/u.test(password) },
  { id: 'lowercase', label: 'At least 1 lowercase letter', test: (password) => /\p{Ll}/u.test(password) },
  { id: 'number', label: 'At least 1 number', test: (password) => /\p{Nd}/u.test(password) },
  {
    id: 'special',
    label: 'At least 1 special character',
    test: (password) => /[^\p{L}\p{Nd}\s]/u.test(password),
  },
  {
    id: 'noSpaces',
    label: 'No spaces',
    test: (password) => password.length > 0 && !/\s/u.test(password),
  },
];

/** Whether every rule is met, and the password is no longer than the server accepts. */
export function meetsPasswordPolicy(password: string): boolean {
  return password.length <= PASSWORD_MAX_LENGTH && PASSWORD_RULES.every((rule) => rule.test(password));
}
